import { z } from 'zod';
/**
 * Unified audit + activity trail. ONE collection (`audit/`) replaces the
 * legacy split between `audit/*` (server-written, superAdmin-read) and
 * `activity/*` (client-written, owner-read). Two access patterns share
 * one schema: events with `userVisible: true` surface in a user's own
 * history feed; everything is visible to platform `superAdmin` for
 * forensics. Writes are server-only — clients never `addDoc('audit')`
 * directly. View-style events from the browser route through
 * `POST /api/audit/events` (batched) so IP/UA/country are only ever
 * stamped server-side and can never be forged by the client.
 *
 * Threat-model note (this is a sovereign-intelligence platform for a
 * transitional-government-in-waiting): we deliberately do NOT store raw
 * IP. We store a salted hash that lets us correlate "same actor across
 * events" within a salt-rotation window, plus 2-letter country only
 * (never city). Raw IP/UA/country fields get NULLED at `redactAfter`
 * (~90 days) by a scheduled function while the action+actor+target
 * survive for the full retention window — the audit trail outlives the
 * surveillance dataset.
 */
/**
 * Top-level grouping that drives default retention + the admin filter.
 * `view` covers user-history-feed events (dossier views, ask queries,
 * graph focus). `data` covers org-scoped mutations (watchlist, signals,
 * tasks). `org` and `platform` cover tenant + platform admin actions.
 * `auth` covers session + token events. `security` covers permission
 * denials, anomalies, and tamper-evident events.
 */
export const AUDIT_CATEGORIES = [
    'auth',
    'org',
    'platform',
    'data',
    'view',
    'security',
];
export const AuditCategory = z.enum(AUDIT_CATEGORIES);
/**
 * Severity gates retention + admin dashboards. `info` is the routine
 * default; `notice` flags noteworthy-but-benign events (e.g. share
 * link opened); `warn` is recoverable failure (auth retry, permission
 * denied); `critical` is escalation-worthy and gets the longest retention.
 */
export const AUDIT_SEVERITIES = ['info', 'notice', 'warn', 'critical'];
export const AuditSeverity = z.enum(AUDIT_SEVERITIES);
export const AUDIT_DEVICE_CLASSES = [
    'desktop',
    'mobile',
    'tablet',
    'bot',
    'unknown',
];
export const AuditDeviceClass = z.enum(AUDIT_DEVICE_CLASSES);
/**
 * Whitelist of action strings the client batch endpoint will accept.
 * Anything else 400s. Two flavors here:
 *
 *   `view.*`    — pure UX events (dossier opened, network panned). They
 *                 surface in the user's history feed and have no
 *                 server-side mutation behind them.
 *   `<obj>.<verb>` (legacy shape, e.g. `note.created`, `thread.forked`)
 *                — actions that today are EXECUTED client-side via direct
 *                 Firestore writes (notes, threads, briefs metadata,
 *                 investigations, overrides). The audit entry records
 *                 the analyst's intent. As individual mutations migrate
 *                 to server endpoints, the corresponding action becomes
 *                 server-only and drops off this list.
 *
 * The server endpoint pins `actorUid` + `orgId` from the verified ID
 * token, so a hostile client can't impersonate a different user — but
 * it CAN claim "I created note X" without actually creating it. That's
 * the same trust boundary as today's direct-Firestore-write status quo;
 * the consolidation doesn't widen it. Tightening (server-mediated
 * mutations for notes/threads/etc.) is a separate refactor.
 */
export const CLIENT_AUDIT_ACTIONS = [
    // Pure view events.
    'view.dossier.view',
    'view.graph.focus',
    'view.investigation.open',
    'view.dossier.viewed',
    'view.cypher.run',
    // Ask flow (started client-side; the server `/api/ask` handler also
    // emits its own `view.ask.query` audit, so `ask.completed` is the
    // useful client-side bookend that captures the answer rendered).
    'ask.submitted',
    'ask.completed',
    // Synthesis.
    'synthesis.generated',
    'synthesis.override.saved',
    // Notes (Firestore-direct today).
    'note.created',
    'note.updated',
    'note.deleted',
    // Threads (Firestore-direct today).
    'thread.created',
    'thread.renamed',
    'thread.forked',
    'thread.deleted',
    // Briefs (composer is Firestore-direct; share token is server-mediated
    // and audited there — `brief.shared` here is a stale duplicate kept on
    // the whitelist only for migration window, will be removed after the
    // client stops emitting it).
    'brief.saved',
    'brief.updated',
    'brief.shared',
    // Investigations (Firestore-direct today).
    'investigation.created',
    'investigation.updated',
    'investigation.archived',
    'investigation.deleted',
    // Entity-resolution candidates. The server already audits the actual
    // mutation in `data.candidate.{promote,reject}`; these client emits
    // are the analyst-intent record (kept user-visible so the activity
    // feed shows "you promoted X").
    'candidate.promoted',
    'candidate.rejected',
    // Agentic tasks.
    'task.started',
    'task.retried',
];
export const ClientAuditAction = z.enum(CLIENT_AUDIT_ACTIONS);
/**
 * Per-action policy applied server-side when a client batch lands.
 * `userVisible` decides whether the entry surfaces in the user's
 * history feed (`GET /api/audit/me`); `severity` drives retention and
 * admin filters.
 *
 * The default — unlisted actions — is `{ userVisible: false, severity:
 * 'info' }`. Update this map when you add to {@link CLIENT_AUDIT_ACTIONS}.
 */
export const CLIENT_AUDIT_POLICY = {
    // Surfaced in the user history feed.
    'view.dossier.view': { userVisible: true, severity: 'info' },
    'view.dossier.viewed': { userVisible: true, severity: 'info' },
    'view.graph.focus': { userVisible: true, severity: 'info' },
    'view.investigation.open': { userVisible: true, severity: 'info' },
    'ask.submitted': { userVisible: true, severity: 'info' },
    'ask.completed': { userVisible: true, severity: 'info' },
    'synthesis.generated': { userVisible: true, severity: 'info' },
    'note.created': { userVisible: true, severity: 'info' },
    'thread.created': { userVisible: true, severity: 'info' },
    'thread.forked': { userVisible: true, severity: 'info' },
    'brief.saved': { userVisible: true, severity: 'notice' },
    'investigation.created': { userVisible: true, severity: 'notice' },
    'candidate.promoted': { userVisible: true, severity: 'notice' },
    'candidate.rejected': { userVisible: true, severity: 'notice' },
    // Forensic only.
    'view.cypher.run': { userVisible: false, severity: 'notice' },
    'synthesis.override.saved': { userVisible: false, severity: 'notice' },
    'note.updated': { userVisible: false, severity: 'info' },
    'note.deleted': { userVisible: false, severity: 'notice' },
    'thread.renamed': { userVisible: false, severity: 'info' },
    'thread.deleted': { userVisible: false, severity: 'notice' },
    'brief.updated': { userVisible: false, severity: 'info' },
    'brief.shared': { userVisible: false, severity: 'notice' }, // dup-of-server; remove after client cutover
    'investigation.updated': { userVisible: false, severity: 'info' },
    'investigation.archived': { userVisible: false, severity: 'notice' },
    'investigation.deleted': { userVisible: false, severity: 'notice' },
    'task.started': { userVisible: false, severity: 'info' },
    'task.retried': { userVisible: false, severity: 'notice' },
};
/**
 * The single audit doc shape. Most fields are server-stamped (`at`,
 * `actorUid`, `ipHash`, `country`, `userAgent`, `deviceClass`, `traceId`,
 * `pruneAfter`, `redactAfter`) — handlers pass an {@link AuditEventInput}
 * and the writer fills the rest. `id` is the Firestore doc id.
 *
 * `orgId` semantics:
 *   - string  — scoped to that tenant (the common case)
 *   - 'platform' — platform-level event with no tenant (e.g. superAdmin
 *                  grant). Stored as a string so admin filters compose
 *                  cleanly.
 *   - null    — unauth public surface (e.g. brief.share.view from /b/:token
 *               where the share token is the entire authz model).
 */
export const AuditEvent = z.object({
    id: z.string(),
    at: z.string(),
    // Actor
    actorUid: z.string(), // 'public' for unauth principals
    actorEmail: z.string().optional(),
    // Tenant
    orgId: z.union([z.string(), z.null()]),
    // Event
    category: AuditCategory,
    action: z.string(), // dotted: 'dossier.view', 'brief.share.create'
    target: z.string().optional(),
    detail: z.record(z.string(), z.unknown()).optional(),
    // Surfacing — splits "appears in user's history feed" from "audit-only".
    userVisible: z.boolean(),
    severity: AuditSeverity,
    // Server-only context (privacy-sensitive — nulled at redactAfter).
    ipHash: z.string().optional(),
    ipSaltVersion: z.string().optional(),
    country: z.string().length(2).optional(), // ISO-3166 alpha-2
    userAgent: z.string().max(500).optional(),
    deviceClass: AuditDeviceClass.optional(),
    traceId: z.string().optional(),
    // Retention. `pruneAfter` is the Firestore TTL field — Firestore deletes
    // the doc at this timestamp. `redactAfter` is the (earlier) timestamp at
    // which a scheduled function nulls the privacy-sensitive fields above.
    pruneAfter: z.string(),
    redactAfter: z.string().optional(),
});
/**
 * Caller-facing input. The writer derives everything else.
 */
export const AuditEventInput = z.object({
    actorUid: z.string(),
    actorEmail: z.string().optional(),
    orgId: z.union([z.string(), z.null()]),
    category: AuditCategory,
    action: z.string(),
    target: z.string().optional(),
    detail: z.record(z.string(), z.unknown()).optional(),
    userVisible: z.boolean().default(false),
    severity: AuditSeverity.default('info'),
});
/**
 * Client batch payload posted to `POST /api/audit/events`. The client
 * supplies only the action + lightweight context; the server pins
 * `actorUid`/`orgId` from the verified ID token and stamps everything
 * else. Batched to avoid one request per dossier-view.
 */
export const ClientAuditEvent = z.object({
    action: ClientAuditAction,
    target: z.string().max(200).optional(),
    detail: z.record(z.string(), z.unknown()).optional(),
});
export const ClientAuditEventBatch = z.object({
    events: z.array(ClientAuditEvent).min(1).max(50),
});
/**
 * Retention windows in days, keyed by category. Drives `pruneAfter` on
 * write; `redactAfter` is fixed at 90 days for everything (raw PII gets
 * nulled at the same horizon regardless of doc retention). `critical`
 * severity overrides category retention to 7 years for legal-hold-friendly
 * preservation.
 */
export const AUDIT_RETENTION_DAYS = {
    view: 90,
    data: 365,
    org: 730,
    platform: 730,
    auth: 730,
    security: 730,
};
export const AUDIT_REDACT_AFTER_DAYS = 90;
export const AUDIT_CRITICAL_RETENTION_DAYS = 365 * 7;
/**
 * Admin list response. `entries` is an array of full {@link AuditEvent}s
 * ordered newest-first, ready for the audit-explorer table.
 */
export const ListAuditEntriesResponse = z.object({
    entries: z.array(AuditEvent),
});
